/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import Button from 'react-bootstrap/Button'

import { billingLocationCodeLookup } from 'components/lab/constants/payors'
import {
  BILLING_PROVIDER_LABELS,
  billedFallback,
  ChargeConfigStatus,
  chargeLocationLabel,
  configEnd,
  configExtendedByDelete,
  configStatus,
  contractedLabel,
  endsBeforeSuccessor,
  formatRateAmount,
  StoredChargeConfig,
  todayIso,
} from 'components/lab/payer/chargeRules/chargeRule'
import { HST_CODE_TIER_LABELS, HST_CODE_TIERS } from 'components/lab/payer/hstCodes/hstCodes'
import { confirm } from 'components/molecules/Confirmation'

/**
 * A configuration as the tables show it: the row plus its derived end, its
 * status today, what a null billed amount bills and whether its own `endsOn`
 * terminated the series. The Charge Rules listing carries the first three from
 * the server; the payer screen works them all out with `toConfigRow` against
 * the payer's whole set.
 */
export type ChargeConfigRow = StoredChargeConfig & {
  effectiveTo: string | null
  status: ChargeConfigStatus
  billedFallback: number | null
  terminated: boolean
}

export const toConfigRow = (
  configs: StoredChargeConfig[],
  config: StoredChargeConfig,
  today = todayIso(),
): ChargeConfigRow => ({
  ...config,
  effectiveTo: configEnd(configs, config),
  status: configStatus(configs, config, today),
  billedFallback: billedFallback(configs, config),
  terminated: endsBeforeSuccessor(configs, config),
})

const isSplitRow = (row: StoredChargeConfig) => row.hstMode === 'SPLIT'

const muted = { fontSize: '0.8rem', color: '#6c757d' }

const STATUS_PILLS: Record<ChargeConfigStatus | 'TERMINATED', [string, string]> = {
  ACTIVE: ['Active', 'badge-success'],
  SCHEDULED: ['Scheduled', 'badge-info'],
  ENDED: ['Ended', 'badge-secondary'],
  TERMINATED: ['Terminated', 'badge-dark'],
}

// Ended on its own `endsOn` rather than because a newer row took over.
const isTerminated = (row: ChargeConfigRow) => row.status === 'ENDED' && row.terminated

const StatusPill = ({ row }: { row: ChargeConfigRow }) => {
  const [label, className] = STATUS_PILLS[isTerminated(row) ? 'TERMINATED' : row.status]

  return <span className={`badge badge-pill ${className}`}>{label}</span>
}

export const effectiveLabel = (row: Pick<ChargeConfigRow, 'effectiveFrom' | 'effectiveTo'>) =>
  `${row.effectiveFrom ?? 'since start'} → ${row.effectiveTo ?? 'open'}`

const cptLabel = (row: StoredChargeConfig) => (isSplitRow(row) ? 'Tiered 95X' : row.cptCode ?? '-')

// A Split row's TC + PC per tier, Low / Moderate / High; null when a tier has no amount.
const tierTotals = (row: StoredChargeConfig, field: 'billedAmount' | 'amount') =>
  HST_CODE_TIERS.map((tier) => {
    const amounts = (row.hstTierRates ?? [])
      .filter((rate) => rate.tier === tier)
      .map((rate) => rate[field])

    return amounts.length && amounts.every((amount) => amount !== null && amount !== undefined)
      ? amounts.reduce<number>((sum, amount) => sum + Number(amount), 0)
      : null
  })

const tierTotalsText = (totals: (number | null)[]) =>
  totals.map((total) => (total === null ? '-' : formatRateAmount(total))).join(' / ')

const TIERS_TITLE = HST_CODE_TIERS.map((tier) => HST_CODE_TIER_LABELS[tier]).join(' / ')

const PricesCell = ({
  row,
  payerInNetwork,
}: {
  row: ChargeConfigRow
  payerInNetwork?: boolean
}) => {
  if (isSplitRow(row)) {
    const rates = tierTotals(row, 'amount')

    return (
      <div title={`Study totals, ${TIERS_TITLE}`}>
        <div>{tierTotalsText(tierTotals(row, 'billedAmount'))} billed</div>
        <div css={muted}>
          {rates.every((rate) => rate === null)
            ? 'No contract rate'
            : `Rate ${tierTotalsText(rates)}`}
          {' · Contracted: '}
          {contractedLabel(row.hstTierRates?.[0]?.contracted, payerInNetwork)}
        </div>
      </div>
    )
  }

  return (
    <>
      {row.billedAmount === null ? (
        <div>
          Not recorded
          <span css={muted}>
            {row.billedFallback === null ? '' : ` · bills ${formatRateAmount(row.billedFallback)}`}
          </span>
        </div>
      ) : (
        <div>{formatRateAmount(row.billedAmount)} billed</div>
      )}
      <div css={muted}>
        {row.contractRate === null
          ? 'No contract rate'
          : `Rate ${formatRateAmount(row.contractRate)}`}
        {' · Contracted: '}
        {contractedLabel(row.contracted, payerInNetwork)}
      </div>
    </>
  )
}

// How the tables and the modal show an HST Global row stored without units (a migrated row).
export const HST_UNITS_NOT_SET = 'Not set'

const hstUnitsLabel = (units: number | null | undefined) =>
  units === null || units === undefined ? HST_UNITS_NOT_SET : String(units)

const ClaimSetupCell = ({ row }: { row: ChargeConfigRow }) => {
  const details = [
    row.billingLocationCode === undefined
      ? undefined
      : billingLocationCodeLookup[row.billingLocationCode],
    BILLING_PROVIDER_LABELS[row.billingProvider ?? ''],
    row.testType === 'HST' && !isSplitRow(row)
      ? `HST units: ${hstUnitsLabel(row.hstUnitsBillable)}`
      : undefined,
  ].filter(Boolean)

  return (
    <>
      <div>{chargeLocationLabel(row.chargeLocationCode)}</div>
      {details.length > 0 && <div css={muted}>{details.join(' · ')}</div>}
    </>
  )
}

// Names the row that runs on in the deleted one's place, and its new end.
const confirmDeleteConfig = (configs: StoredChargeConfig[], row: ChargeConfigRow) => {
  const extended = configExtendedByDelete(configs, row)
  const runsOn = extended
    ? ` ${extended.config.testType} ${cptLabel(extended.config)}, ${
        extended.config.effectiveFrom ?? 'since start'
      }, will then run to ${extended.end ?? 'open'}.`
    : ''

  // A function, so confirm does not append its own "charge ?" to the text.
  return confirm('charge', {
    description: () =>
      `Delete the ${row.testType} ${cptLabel(row)} charge effective ${effectiveLabel(row)}?${runsOn}`,
  })
}

export type ChargeConfigActions<Row extends ChargeConfigRow> = {
  onEdit: (row: Row) => void
  onNewFrom: (row: Row) => void
  onDelete: (row: Row) => void
  /** The row's payer's whole set, ended rows included, which says what runs on after a delete. */
  configsOf: (row: Row) => StoredChargeConfig[] | Promise<StoredChargeConfig[]>
}

type Cell<Row> = { row: { original: Row } }

/**
 * The columns both charge tables share: the payer's Charges tab and the
 * cross-payer Charge Rules screen. Actions are left off for a viewer who
 * cannot edit.
 */
export const chargeConfigColumns = <Row extends ChargeConfigRow>({
  payerInNetwork,
  actions,
}: {
  /** Resolves a contracted flag left to the payer; unknown on the cross-payer list. */
  payerInNetwork?: boolean
  actions?: ChargeConfigActions<Row>
}) => [
  {
    accessor: 'status',
    Header: 'Status',
    Cell: ({ row }: Cell<Row>) => <StatusPill row={row.original} />,
  },
  {
    accessor: 'effectiveFrom',
    Header: 'Effective',
    Cell: ({ row }: Cell<Row>) => (
      <span css={{ whiteSpace: 'nowrap' }}>{effectiveLabel(row.original)}</span>
    ),
  },
  {
    accessor: 'testType',
    Header: 'Test Type',
    Cell: ({ row }: Cell<Row>) => (
      <>
        {row.original.testType}
        {row.original.isPrimary && <span className="badge badge-primary ml-2">Primary</span>}
      </>
    ),
  },
  {
    accessor: 'cptCode',
    Header: 'CPT',
    Cell: ({ row }: Cell<Row>) => <>{cptLabel(row.original)}</>,
  },
  {
    accessor: 'billedAmount',
    Header: 'Billed · Rate · Contracted',
    Cell: ({ row }: Cell<Row>) => <PricesCell row={row.original} payerInNetwork={payerInNetwork} />,
  },
  {
    accessor: 'chargeLocationCode',
    Header: 'Claim Setup',
    Cell: ({ row }: Cell<Row>) => <ClaimSetupCell row={row.original} />,
  },
  ...(actions
    ? [
        {
          accessor: 'actions',
          Header: '',
          Cell: ({ row }: Cell<Row>) => (
            <div className="d-flex justify-content-end" css={{ gap: 6, whiteSpace: 'nowrap' }}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => actions.onEdit(row.original)}>
                Edit
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                css={{ whiteSpace: 'nowrap' }}
                onClick={() => actions.onNewFrom(row.original)}>
                New from this
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                title="Delete"
                aria-label="Delete"
                onClick={() => {
                  Promise.resolve(actions.configsOf(row.original))
                    .catch(() => [])
                    .then((configs) => confirmDeleteConfig(configs, row.original))
                    .then(() => actions.onDelete(row.original))
                    .catch(() => {})
                }}>
                <i className="fas fa-trash" />
              </Button>
            </div>
          ),
        },
      ]
    : []),
]

/**
 * Whether a table lists ended configurations too; off by default and
 * remembered per browser. Storage can be unavailable (private mode, blocked
 * cookies), in which case the choice lasts the visit only.
 */
export const useShowEnded = (storageKey: string) => {
  const [showEnded, setShowEnded] = useState(() => {
    try {
      return window.localStorage.getItem(storageKey) === 'true'
    } catch {
      return false
    }
  })

  const update = (value: boolean) => {
    setShowEnded(value)

    try {
      window.localStorage.setItem(storageKey, String(value))
    } catch {
      // Not remembered; the toggle still works for this visit.
    }
  }

  return [showEnded, update] as const
}

export const ShowEndedCheckbox = ({
  checked,
  onChange,
  disabled,
  title,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  title?: string
}) => (
  <label
    className="mb-0 d-flex align-items-center"
    title={title}
    css={{
      userSelect: 'none',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}>
    <input
      type="checkbox"
      className="mr-2"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    Show ended
  </label>
)
